import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableTeamSelect } from './SearchableTeamSelect';
import { Label } from '@/components/ui/label';
import { useGetFeedLeaguesQuery, useGetFeedTeamsQuery, useGetFeedPlayersQuery } from '@/api/feed';
import { resolvePlayerImageUrl, resolveThirdImageUrl, type Sport } from '@/utils/jersey-mapping';

interface PlayerSelection {
  playerName: string;
  playerId?: string; // Player externalId
  playerPosition?: string; // Player position
  teamId?: string; // Team externalId (for grading_data)
  jerseyNo: number;
  teamName: string;
  teamAbbr: string;
  jerseyUrl: string;
  teamLogoUrl: string;
  sport: string;
}

interface PlayerSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (selection: PlayerSelection) => void;
  selectedTeams?: string[];
  fieldLabel?: string;
  /** When set, auto-selects this league and disables the league selector (e.g., when pickem sport is not "Mixed") */
  restrictedLeagueId?: string;
  /** Contest League IP flag — IP on bakes headshots (where available) + team logos; off bakes jerseys + avatars */
  leagueIp?: boolean;
}

export function PlayerSelectorModal({
  open,
  onOpenChange,
  onSelect,
  selectedTeams = [],
  fieldLabel = 'Option',
  restrictedLeagueId,
  leagueIp = false,
}: PlayerSelectorModalProps) {
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);

  // Auto-select restricted league when modal opens
  useEffect(() => {
    if (open && restrictedLeagueId && !selectedLeagueId) {
      setSelectedLeagueId(restrictedLeagueId);
    }
  }, [open, restrictedLeagueId, selectedLeagueId]);

  // Fetch data from feed service
  const { data: leagues = [], isLoading: isLoadingLeagues } = useGetFeedLeaguesQuery(undefined);
  const { data: teams = [], isLoading: isLoadingTeams } = useGetFeedTeamsQuery(selectedLeagueId);
  const { data: players = [], isLoading: isLoadingPlayers } =
    useGetFeedPlayersQuery(selectedTeamId);

  // Filter teams to only show teams from fixture if fixture team IDs are provided
  const filteredTeams =
    selectedTeams.length === 2
      ? teams.filter(team => selectedTeams.includes(team.externalId))
      : teams;

  // Reset downstream selections when parent changes
  useEffect(() => {
    setSelectedTeamId('');
    setSelectedPlayerId('');
  }, [selectedLeagueId]);

  useEffect(() => {
    setSelectedPlayerId('');
  }, [selectedTeamId]);

  // Find selected entities
  const selectedLeague = leagues.find(l => l.externalId === selectedLeagueId);
  const selectedTeam = teams.find(t => t.externalId === selectedTeamId);
  const selectedPlayer = players.find(p => p.externalId === selectedPlayerId);

  // Gate Select on the same objects handleSelect needs (NOT on previewUrl, so
  // numberless soccer players with no auto-headshot stay selectable). Gating on
  // selectedPlayerId alone enabled the button while selectedLeague was still
  // undefined (e.g. leagues list slow/failed under a restrictedLeagueId), so a
  // click silently no-op'd. (DKPLAT-396)
  const canSelect = !!selectedLeague && !!selectedTeam && !!selectedPlayer;

  // Generate preview URL when team and player are selected. The image follows
  // the contest's League IP setting: headshot (where available) when on, jersey
  // when off — with the resolver applying the fail-safe per sport.
  useEffect(() => {
    if (selectedTeam && selectedPlayer && selectedLeague) {
      const sport = selectedLeague.sportId as Sport;
      // A player-specific image is available via a headshot (IP-on MLB with a
      // name) or a numbered jersey. Without either we'd only get a team-level
      // avatar — identical for every player on the team — so don't auto-fill;
      // the admin can still set an image via the manual jersey/upload picker.
      const hasHeadshot =
        leagueIp &&
        sport.toLowerCase() === 'mlb' &&
        !!selectedPlayer.firstName &&
        !!selectedPlayer.lastName;
      if (!hasHeadshot && !selectedPlayer.jerseyNumber) {
        setPreviewUrl('');
        return;
      }
      const url = resolvePlayerImageUrl({
        teamAbbr: selectedTeam.abbreviation || '',
        sport,
        jerseyNo: selectedPlayer.jerseyNumber,
        firstName: selectedPlayer.firstName,
        lastName: selectedPlayer.lastName,
        leagueIp,
      });
      setPreviewUrl(url ?? '');
      setImageError(false);
    } else {
      setPreviewUrl('');
    }
  }, [selectedTeam, selectedPlayer, selectedLeague, leagueIp]);

  const handleSelect = () => {
    if (selectedTeam && selectedPlayer && selectedLeague) {
      const sport = selectedLeague.sportId as Sport;
      const teamLogoUrl = resolveThirdImageUrl({
        teamAbbr: selectedTeam.abbreviation || '',
        sport,
        leagueIp,
      });

      // Image is optional: many players (e.g. soccer) have no jersey number, so
      // no auto-preview is generated. Save an empty image (the option falls back
      // to /no-jersey.png) and let the admin set one via the jersey/upload
      // pickers. Never persist a preview URL that failed to load.
      const jerseyUrl = previewUrl && !imageError ? previewUrl : '';

      onSelect({
        playerName: selectedPlayer.displayName,
        playerId: selectedPlayer.externalId,
        playerPosition: selectedPlayer.position,
        teamId: selectedTeam.externalId,
        jerseyNo: selectedPlayer.jerseyNumber ? parseInt(selectedPlayer.jerseyNumber, 10) : 0,
        teamName: selectedTeam.name,
        teamAbbr: selectedTeam.abbreviation || '',
        jerseyUrl,
        teamLogoUrl,
        sport: selectedLeague.sportId,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    // Only reset league if not restricted
    if (!restrictedLeagueId) {
      setSelectedLeagueId('');
    }
    setSelectedTeamId('');
    setSelectedPlayerId('');
    setPreviewUrl('');
    setImageError(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Select Player {fieldLabel && `for ${fieldLabel}`}</DialogTitle>
          <DialogDescription>
            Choose a league, team, and player from the sports feed
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="league-select">League</Label>
            {restrictedLeagueId && (
              <p className="text-xs text-muted-foreground mb-1">
                Pre-selected based on pickem sport
              </p>
            )}
            <Select
              value={selectedLeagueId}
              onValueChange={setSelectedLeagueId}
              disabled={isLoadingLeagues || !!restrictedLeagueId}
            >
              <SelectTrigger id="league-select">
                <SelectValue
                  placeholder={isLoadingLeagues ? 'Loading leagues...' : 'Select a league'}
                />
              </SelectTrigger>
              <SelectContent>
                {leagues.map(league => (
                  <SelectItem key={league.externalId} value={league.externalId}>
                    {league.name} ({league.sportId})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLeagueId && (
            <div className="space-y-2">
              <Label htmlFor="team-select">Team</Label>
              {selectedTeams.length === 2 && (
                <p className="text-xs text-muted-foreground mb-2">
                  Teams filtered by selected fixture
                </p>
              )}
              <SearchableTeamSelect
                teams={filteredTeams}
                value={selectedTeamId}
                onValueChange={setSelectedTeamId}
                disabled={isLoadingTeams}
                placeholder={isLoadingTeams ? 'Loading teams...' : 'Select a team'}
                id="team-select"
              />
            </div>
          )}

          {selectedTeamId && (
            <div className="space-y-2">
              <Label htmlFor="player-select">Player</Label>
              <Select
                value={selectedPlayerId}
                onValueChange={setSelectedPlayerId}
                disabled={isLoadingPlayers}
              >
                <SelectTrigger id="player-select">
                  <SelectValue
                    placeholder={isLoadingPlayers ? 'Loading players...' : 'Select a player'}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {players.map(player => (
                    <SelectItem key={player.externalId} value={player.externalId}>
                      {player.displayName}
                      {player.jerseyNumber && ` #${player.jerseyNumber}`}
                      {player.position && ` (${player.position})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative w-full p-4 h-64 border rounded-md overflow-hidden bg-gray-50">
                {!imageError ? (
                  <img
                    src={previewUrl}
                    alt="Player jersey preview"
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center text-sm text-muted-foreground">
                    Failed to load jersey image
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground break-all">{previewUrl}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!canSelect}
            title={
              !canSelect
                ? 'Choose a player to enable'
                : 'Confirm this player. The image is optional — if none is auto-generated you can set one with the jersey or upload picker.'
            }
          >
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
